<template>
  <MDropdown v-if="!disabled" ref="dropdownMenu">
    <template v-slot:trigger>
      <MButton
        id="action-btn"
        variant="neutral-lighter"
        :shadow="false"
        shape="circle"
      >
        <MIcon name="ellipsis-v" size="2x" />
      </MButton>
    </template>
    <template v-slot:menu>
      <MMenu class="action-menu">
        <MMenuItem v-if="allowAskForApproval">
          <ApprovalAction
            :module-name="moduleName"
            :parent-id="asset.id"
            :resource-name="
              moduleName === $constants.CMDB ? asset.ciName : asset.name
            "
            @trigger="hide"
            @done="$emit('approval-changed')"
          />
        </MMenuItem>
        <MMenuItem
          v-if="
            !asset.barCode &&
            [$constants.ASSET_CONSUMABLE, $constants.CMDB].indexOf(
              moduleName
            ) === -1
          "
        >
          <ChangeBarcode
            :module-name="moduleName"
            :asset="asset"
            @submit="$emit('update', $event)"
            @refresh="$emit('refresh')"
            @trigger="hide"
          >
            <template v-slot:trigger>
              <MIcon name="barcode" class="action-menu-icon" />
              <span class="ml-1 action-menu-text">
                {{ $tc('add') }} {{ $t('barcode') }}
              </span>
            </template>
          </ChangeBarcode>
        </MMenuItem>
        <MMenuItem
          v-if="
            tenantPrefrences.qrCodeSupport &&
            [$constants.ASSET_CONSUMABLE, $constants.CMDB].indexOf(
              moduleName
            ) === -1
          "
        >
          <PrintQrCode
            :resource-id="asset.id"
            :resource="asset"
            :module-name="moduleName"
            @done="hide"
          />
        </MMenuItem>
        <MMenuItem
          v-if="
            !disabled &&
            allowScanAndChangeLog &&
            !asset.excludedFromScan &&
            ['aws', 'azure'].indexOf(
              assetConfigInfo.discoveryDeviceProtocol
            ) === -1
          "
        >
          <ScanNow
            :resource-id="asset.id"
            :module-name="moduleName"
            :asset="asset"
            :update-api="updateApi"
            @trigger="hide"
          />
        </MMenuItem>
        <template
          v-if="
            asset &&
            !(
              moduleName !== 'asset_hardware' ||
              (moduleName === 'asset_hardware' &&
                asset.origin !== 'agent' &&
                (!asset.agentActiveStatus ||
                  (asset.agentActiveStatus &&
                    asset.agentActiveStatus === 'not_applicable')))
            )
          "
        >
          <MMenuItem v-for="i in systemActions" :key="i.action">
            <SystemAction
              :resource-id="asset.id"
              :module-name="moduleName"
              :asset="asset"
              :icon-name="i.icon"
              :system-action="i.action"
              @trigger="hide"
            />
          </MMenuItem>
        </template>
        <MMenuItem
          v-if="
            moduleName !== $constants.CMDB &&
            assetConfigInfo.discoveryDeviceProtocol === 'wmi'
          "
        >
          <WakeUpNow
            :resource-id="asset.id"
            :module-name="moduleName"
            @trigger="hide"
          />
        </MMenuItem>
        <MMenuItem
          v-if="
            !disabled &&
            moduleName === $constants.ASSET_HARDWARE &&
            (asset.underChangeControl === true ||
              showVarianceDetection === true)
          "
        >
          <RunVarianceDetection
            :resource-id="asset.id"
            :module-name="moduleName"
            bind-shortcut
            @trigger="hide"
            @done="handleVarianceDetection"
          />
        </MMenuItem>
        <MMenuItem
          v-if="
            !disabled &&
            allowScanAndChangeLog &&
            ['aws', 'azure'].indexOf(
              assetConfigInfo.discoveryDeviceProtocol
            ) === -1
          "
        >
          <ExcludeIncludeFromScan
            :resource-id="asset.id"
            :module-name="moduleName"
            :asset="asset"
            @trigger="hide"
            @done="handleExcludeIncludeAction"
          />
        </MMenuItem>
        <MMenuItem
          v-if="moduleName !== $constants.CMDB && availableRemoteDesktop"
        >
          <RemoteDesktop
            :resource-id="asset.id"
            :module-name="moduleName"
            bind-shortcut
            :handle-remote-dasktop-action="handleRemoteDasktopAction"
            @trigger="hide"
          />
        </MMenuItem>
        <MMenuItem
          v-if="moduleName !== $constants.CMDB && availableRemoteDesktop"
        >
          <RdpHistory
            :resource-id="asset.id"
            :asset="asset"
            :module-name="moduleName"
            bind-shortcut
            @trigger="hide"
          />
        </MMenuItem>
        <MMenuItem
          v-if="
            moduleName === $constants.ASSET_HARDWARE &&
            asset.origin !== 'purchase'
          "
        >
          <ReconcileDrawer
            :module-name="moduleName"
            :asset="asset"
            @refresh="$emit('refresh')"
            @trigger="hide"
          />
        </MMenuItem>
        <MMenuItem
          v-if="
            [
              $constants.ASSET_HARDWARE,
              $constants.ASSET_NON_IT,
              $constants.CMDB,
            ].indexOf(moduleName) >= 0
          "
        >
          <UsedByHistoryDrawer
            :asset="asset"
            :module-name="moduleName"
            @trigger="hide"
          />
        </MMenuItem>
        <MMenuItem
          v-if="
            [
              $constants.ASSET_HARDWARE,
              $constants.ASSET_NON_IT,
              $constants.CMDB,
            ].indexOf(moduleName) >= 0
          "
        >
          <LocationHistoryDrawer
            :asset="asset"
            :module-name="moduleName"
            @trigger="hide"
          />
        </MMenuItem>
        <MMenuItem
          v-if="
            moduleName === $constants.ASSET_HARDWARE && asset.origin === 'agent'
          "
        >
          <ActionHistoryDrawer
            :asset="asset"
            :module-name="moduleName"
            @trigger="hide"
          />
        </MMenuItem>
        <MMenuItem v-if="!disabled">
          <Archive
            :resource-id="asset.id"
            :module-name="moduleName"
            :asset-origin="asset.origin"
            is-detail
            bind-shortcut
            @trigger="hide"
            @done="handleArchive"
          />
        </MMenuItem>
        <MMenuItem>
          <Print
            :asset-type-id="asset.assetTypeId"
            :resource-id="asset.id"
            :resource="asset"
            :module-name="moduleName"
            @trigger="hide"
            @done="hide"
          />
        </MMenuItem>
      </MMenu>
    </template>
  </MDropdown>
</template>

<script>
import Bus from '@utils/emitter'
import { authComputed } from '@state/modules/auth'
import { AssetTypeComputed } from '@state/modules/asset-type'
import { PreferenceComputed } from '@state/modules/preference'
import ApprovalAction from '@components/approval/approval-action'
import { establishRDPSessionApi } from '../asset-api'
import Print from './action-menu-items/print'
import PrintQrCode from './action-menu-items/print-qr-code.vue'
import Archive from './action-menu-items/archive'
import RemoteDesktop from './action-menu-items/remote-desktop'
import RdpHistory from './action-menu-items/rdp-history.vue'
import ChangeBarcode from './action-menu-items/change-barcode'
import ReconcileDrawer from './action-menu-items/reconcile'
import UsedByHistoryDrawer from './action-menu-items/used-by-history-drawer'
import ActionHistoryDrawer from './action-menu-items/action-history-drawer'
import LocationHistoryDrawer from './action-menu-items/location-history-drawer'
import ExcludeIncludeFromScan from './action-menu-items/exclude-include-from-scan'
import ScanNow from './action-menu-items/scan-now'
import WakeUpNow from './action-menu-items/wake-up-now'
import RunVarianceDetection from './action-menu-items/run-variance-detection'
import SystemAction from './action-menu-items/system-action'
import {
  COMPUTER_PROPERTY_GROUP,
  MOBILE_DEVICE_PROPERTY_GROUP,
} from './../helpers/asset'
import { isAvailableRDP } from './../helpers/asset-type'

export default {
  name: 'ActionMenu',
  components: {
    Print,
    PrintQrCode,
    Archive,
    ChangeBarcode,
    ReconcileDrawer,
    ExcludeIncludeFromScan,
    ScanNow,
    RunVarianceDetection,
    RemoteDesktop,
    RdpHistory,
    ApprovalAction,
    UsedByHistoryDrawer,
    LocationHistoryDrawer,
    ActionHistoryDrawer,
    WakeUpNow,
    SystemAction,
  },
  props: {
    moduleName: { type: String, required: true },
    asset: { type: Object, required: true },
    disabled: { type: Boolean, default: false },
    updateApi: { type: Function, default: undefined },
    assetConfigInfo: { type: Object, required: true },
  },
  data() {
    this.systemActions = [
      { action: 'lock', icon: 'lock-alt' },
      { action: 'restart', icon: 'undo' },
      { action: 'shutdown', icon: 'power-off' },
      { action: 'sleep', icon: 'moon' },
    ]
    return {
      showVarianceDetection: false,
    }
  },
  computed: {
    ...authComputed,
    ...AssetTypeComputed,
    ...PreferenceComputed,
    availableRemoteDesktop() {
      return (
        isAvailableRDP(this.assetTypes, this.asset.assetTypeId) &&
        this.asset.ipAddress
      )
    },
    allowScanAndChangeLog() {
      return (
        (this.assetConfigInfo.property || []).indexOf(
          COMPUTER_PROPERTY_GROUP
        ) >= 0 ||
        (this.assetConfigInfo.property || []).indexOf(
          MOBILE_DEVICE_PROPERTY_GROUP
        ) >= 0 ||
        this.assetConfigInfo.discoveryDeviceProtocol === 'snmp'
      )
    },
    allowAskForApproval() {
      return (
        [
          this.$constants.ASSET_HARDWARE,
          this.$constants.CMDB,
          this.$constants.ASSET_NON_IT,
        ].indexOf(this.moduleName) >= 0 && !this.disabled
      )
    },
  },
  created() {
    const handler = (data) => {
      this.showVarianceDetection = data
    }
    Bus.$on('asset:run-variance-detection', handler)
    this.$off('hook:beforeDestroy', () => {
      Bus.$off('asset:run-variance-detection', handler)
    })
  },
  methods: {
    hide() {
      this.$refs.dropdownMenu.hide()
    },
    handleVarianceDetection() {
      this.$emit('variance-detection')
    },
    handleArchive(data) {
      this.$emit('archive', data)
    },
    handleExcludeIncludeAction() {
      this.$emit('update', { excludedFromScan: !this.asset.excludedFromScan })
    },
    handleRemoteDasktopAction() {
      return establishRDPSessionApi(this.asset.id).then((data) => {
        if (data.configType === 'vnc') {
          window.open(
            `/api/flotordp/#/client/${data.clientId}?userId=${this.user.id}`,
            '_blank'
          )
        } else {
          window.open(`${data.url}`, '_blank')
        }
      })
    },
  },
}
</script>
